import React, { useRef } from 'react';
import { WIZARD_STEPS } from '../../../enums/wizardSteps';
import type { WizardStepId, WizardStepConfig } from '../../../types/wizardTypes';
import { Check } from 'lucide-react';
import styles from './WizardStepper.module.css';

interface WizardStepperProps {
  currentStep: number;
  completedSteps: Set<WizardStepId>;
  onStepClick: (index: number) => void;
  errorSteps?: Set<number>;
  steps?: WizardStepConfig[];
}

const COMPACT_STEP_LABELS: Record<WizardStepId, string> = {
  'tool-info': 'Tool Info',
  'request-mapping': 'Request',
  'input-fields': 'Input Fields',
  'response-mapping': 'Response',
  'chat-quick-actions': 'Quick Actions',
  'review': 'Review',
};

export default function WizardStepper({ currentStep, completedSteps, onStepClick, errorSteps = new Set<number>(), steps }: WizardStepperProps) {
  const stepsToRender = steps || WIZARD_STEPS;
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    isMouseDown: false,
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    shouldSuppressClick: false,
  });

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    dragStateRef.current = {
      isMouseDown: true,
      isDragging: false,
      startX: event.clientX,
      startScrollLeft: viewport.scrollLeft,
      shouldSuppressClick: false,
    };
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    const dragState = dragStateRef.current;
    if (!viewport || !dragState.isMouseDown) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    if (!dragState.isDragging && Math.abs(deltaX) > 6) {
      dragState.isDragging = true;
      dragState.shouldSuppressClick = true;
    }

    if (!dragState.isDragging) {
      return;
    }

    viewport.scrollLeft = dragState.startScrollLeft - deltaX;
    event.preventDefault();
  };

  const handleMouseEnd = () => {
    const dragState = dragStateRef.current;
    dragState.isMouseDown = false;
    dragState.isDragging = false;
  };

  const handleStepClick = (index: number) => {
    if (dragStateRef.current.shouldSuppressClick) {
      dragStateRef.current.shouldSuppressClick = false;
      return;
    }

    onStepClick(index);
  };

  return (
    <nav className={styles.stepper} aria-label="Wizard sections">
      <div
        ref={viewportRef}
        className={styles.stepperViewport}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseEnd}
        onMouseLeave={handleMouseEnd}
      >
        <div className={styles.stepperTrack} role="tablist" aria-orientation="horizontal">
          {stepsToRender.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = completedSteps.has(step.id);
            const hasError = errorSteps.has(index);
            const tabPanelId = `wizard-panel-${step.id}`;
            const tabId = `wizard-tab-${step.id}`;

            return (
              <div key={step.id} className={styles.stepSlide}>
                <button
                  type="button"
                  className={`${styles.step} ${isActive ? styles.stepActive : ''} ${isCompleted ? styles.stepCompleted : ''} ${hasError ? styles.stepError : ''}`}
                  onClick={() => handleStepClick(index)}
                  role="tab"
                  id={tabId}
                  aria-selected={isActive}
                  aria-controls={tabPanelId}
                  tabIndex={isActive ? 0 : -1}
                  aria-label={`${step.label}${hasError ? ' (has errors)' : ''}`}
                >
                  <span className={styles.stepMeta}>
                    <span className={styles.stepLabel}>{step.label}</span>
                    <span className={styles.stepLabelCompact}>{COMPACT_STEP_LABELS[step.id]}</span>
                  </span>
                  <span className={styles.stepStatus} aria-hidden="true">
                    {hasError ? '!' : isCompleted ? <Check size={14} /> : index + 1}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
